// src/utils/firewallDetection.ts - Detect corporate firewall blocking and provide fallback messaging

export interface FirewallDetectionResult {
  isFirewallLikely: boolean;
  reason: 'firewall' | 'offline' | 'unknown';
}

/**
 * Detects whether a connection failure is likely caused by a corporate firewall.
 *
 * Strategy: If the browser reports online but we can't reach Supabase,
 * the network is selectively blocking outbound requests — a firewall signature.
 */
export async function detectFirewall(): Promise<FirewallDetectionResult> {
  // If the browser itself says offline, it's a connectivity issue, not a firewall
  if (!navigator.onLine) {
    return { isFirewallLikely: false, reason: 'offline' };
  }

  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  if (!supabaseUrl) {
    return { isFirewallLikely: false, reason: 'unknown' };
  }

  try {
    // Attempt a lightweight probe to the Supabase domain
    // Using a short timeout — firewalls often cause hangs rather than clean rejections
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 5000);

    const response = await fetch(`${supabaseUrl}/functions/v1/ai-chat`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ message: 'ping', sessionId: 'firewall-check' }),
      signal: controller.signal,
    });

    clearTimeout(timeout);

    // If we get ANY HTTP response (even 4xx/5xx), the network path is open — not a firewall
    // A 401/403 from Supabase means the request reached the server
    if (response.status > 0) {
      return { isFirewallLikely: false, reason: 'unknown' };
    }

    return { isFirewallLikely: true, reason: 'firewall' };
  } catch (error: any) {
    // Network-level failures when browser is "online" = strong firewall signal
    const errorMessage = error?.message?.toLowerCase() || '';

    const firewallSignals = [
      'failed to fetch',       // Blocked outbound request
      'networkerror',          // Generic network block
      'network request failed',
      'load failed',           // Safari variant
      'aborted',               // Our timeout fired — request hung (firewall dropping packets)
      'err_blocked',           // Chrome ERR_BLOCKED_BY_CLIENT or similar
      'err_connection_refused',
      'err_connection_reset',
      'err_cert',              // SSL inspection causing cert mismatch
      'err_ssl',
      'err_tunnel',            // Proxy tunnel failures
    ];

    const isFirewallSignal = firewallSignals.some(signal => errorMessage.includes(signal));

    // Double-check: can we reach a well-known public endpoint?
    // If yes → selective blocking (firewall). If no → general connectivity issue.
    if (isFirewallSignal) {
      try {
        const publicCheck = await fetch('https://www.google.com/generate_204', {
          method: 'HEAD',
          mode: 'no-cors',
          signal: AbortSignal.timeout(3000),
        });
        // If this succeeds (even opaque response), we have internet but Supabase is blocked
        return { isFirewallLikely: true, reason: 'firewall' };
      } catch {
        // Can't reach Google either — probably no internet, not a firewall
        return { isFirewallLikely: false, reason: 'offline' };
      }
    }

    return { isFirewallLikely: false, reason: 'unknown' };
  }
}

/**
 * Pool of friendly, professional messages that acknowledge firewall restrictions.
 * Each conveys: "I anticipated this" + "here's what you can do."
 *
 * Two messages are randomly selected and displayed together.
 */
const firewallMessages: string[] = [
  "It looks like your network is blocking the AI chat — this is common on corporate and government networks that restrict external API connections.",

  "Your organization's network security appears to be preventing the AI chat from connecting. This is something I see with enterprise firewalls — totally expected.",

  "Looks like there's a firewall between us. Corporate and federal networks often block the external services this chat relies on — I built this site knowing that would happen.",

  "The AI chat can't connect from your current network. If you're on a corporate or government network, their firewall is likely blocking the outbound connection to my AI service.",

  "Your network's security policies seem to be blocking this chat feature. This is common on enterprise networks that restrict external API connections.",

  "Can't reach the AI service from your network — this typically happens behind enterprise firewalls or VPNs that restrict outbound traffic to external APIs.",

  "Network restrictions are preventing the AI chat from loading. If you're at the office, your IT team's firewall is likely the reason — enterprise networks commonly block external API services.",

  "The connection to the AI chat is being blocked, likely by your network's security infrastructure. This typically happens on corporate or government networks with strict outbound policies.",
];

/**
 * Returns 2 randomly selected firewall messages (never duplicates).
 */
export function getRandomFirewallMessages(): [string, string] {
  const shuffled = [...firewallMessages].sort(() => Math.random() - 0.5);
  return [shuffled[0], shuffled[1]];
}
